/**
 * ConsoleLayout — 학원장 PC 관리 콘솔 (데스크탑 전용)
 *
 * - 모바일 /app/biz 와 같은 데이터(academyService 멀티테넌트)를 큰 화면에서 관리
 * - 좌측 고정 사이드바 + 학원 선택 + 상단바
 * - 자식 페이지에 선택된 학원을 Outlet context 로 전달: const { academy } = useOutletContext()
 */
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Navigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import { RxDashboard } from 'react-icons/rx';
import { HiOutlineUsers, HiOutlineBookOpen, HiOutlineCalendarDays, HiOutlineBuildingStorefront } from 'react-icons/hi2';
import { IoChevronDown, IoPhonePortraitOutline, IoCheckmark } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import { resolveAccountType } from '../../services/userProfileService';
import ConsoleRegister from '../../pages/Console/ConsoleRegister';

const CONSOLE_KEY = 'topgrade.consoleAcademy';

const NAV = [
  { path: '/console', label: '운영 대시보드', icon: RxDashboard, end: true },
  { path: '/console/classes', label: '수업 관리', icon: HiOutlineBookOpen },
  { path: '/console/students', label: '학생 관리', icon: HiOutlineUsers },
  { path: '/console/schedule', label: '일정 / 시간표', icon: HiOutlineCalendarDays },
  { path: '/console/settings', label: '학원 정보', icon: HiOutlineBuildingStorefront },
];

/** 자식 페이지에서 선택된 학원을 꺼내쓰는 헬퍼 */
export const useConsoleAcademy = () => useOutletContext();

const ConsoleLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData, loading: authLoading } = useAuth();
  const { operatedAcademies, operatedAcademy, loading: acaLoading, reloadAcademies } = useAcademy();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(() => {
    try { return localStorage.getItem(CONSOLE_KEY) || null; } catch { return null; }
  });

  if (authLoading || acaLoading) {
    return <Splash>불러오는 중…</Splash>;
  }
  if (!currentUser) {
    return <Navigate to="/app/login" replace />;
  }
  // 학원(원장) 계정 전용 — 학부모/학생 등 비학원 계정은 콘솔 진입 차단
  if (resolveAccountType(userData) !== 'academy') {
    return <Navigate to="/app/home" replace />;
  }
  // 운영 중인 학원이 없으면 — 데스크탑 와이드 등록 폼 (모바일로 안 튕김)
  if (!operatedAcademy) {
    return <ConsoleRegister />;
  }

  const academy = operatedAcademies.find((a) => a.id === selectedId) || operatedAcademy;
  const ownerName = userData?.name || '원장';

  const pick = (id) => {
    setSelectedId(id);
    try { localStorage.setItem(CONSOLE_KEY, id); } catch {}
    setPickerOpen(false);
  };

  const roleLabel = { owner: '원장', teacher: '강사', assistant: '조교' }[academy.myRole] || '운영';
  const pending = academy.status && academy.status !== 'approved';

  return (
    <Container data-fullwidth>
      <Sidebar>
        <LogoArea onClick={() => navigate('/console')}>
          <LogoIcon>1급</LogoIcon>
          <div>
            <LogoText>일등급</LogoText>
            <LogoSub>학원 콘솔</LogoSub>
          </div>
        </LogoArea>

        <AcademyPicker>
          <PickerBtn type="button" onClick={() => operatedAcademies.length > 1 && setPickerOpen((o) => !o)} $multi={operatedAcademies.length > 1}>
            <PickerThumb $url={academy.photoUrl}>{!academy.photoUrl && (academy.name?.charAt(0) || '학')}</PickerThumb>
            <PickerInfo>
              <PickerName>{academy.name || '내 학원'}</PickerName>
              <PickerRole>{roleLabel}</PickerRole>
            </PickerInfo>
            {operatedAcademies.length > 1 && <IoChevronDown size={16} color="#9CA3AF" />}
          </PickerBtn>
          {pickerOpen && (
            <PickerMenu>
              {operatedAcademies.map((a) => (
                <PickerItem key={a.id} type="button" onClick={() => pick(a.id)} $on={a.id === academy.id}>
                  <span>{a.name || '내 학원'}</span>
                  {a.id === academy.id && <IoCheckmark size={16} color="#4A7CFE" />}
                </PickerItem>
              ))}
            </PickerMenu>
          )}
        </AcademyPicker>

        <Nav>
          {NAV.map((item) => {
            const isActive = item.end
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            return (
              <NavItem key={item.path} $active={isActive} onClick={() => navigate(item.path)}>
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavItem>
            );
          })}
        </Nav>

        <SidebarFooter>
          <FooterBtn onClick={() => navigate('/app/biz')}>
            <IoPhonePortraitOutline size={16} />
            모바일 앱으로
          </FooterBtn>
        </SidebarFooter>
      </Sidebar>

      <MainArea>
        <TopBar>
          <TopTitle>
            {academy.name || '내 학원'}
            {pending && <PendingTag>승인 대기중</PendingTag>}
          </TopTitle>
          <ProfileChip>
            <ProfileAvatar>{ownerName.charAt(0)}</ProfileAvatar>
            <span>{ownerName} {roleLabel}</span>
          </ProfileChip>
        </TopBar>
        <Content>
          <Outlet context={{ academy, reloadAcademies }} />
        </Content>
      </MainArea>
    </Container>
  );
};

export default ConsoleLayout;

const Splash = styled.div`
  display: flex; align-items: center; justify-content: center;
  min-height: 100vh; color: #9CA3AF; font-size: 15px;
`;

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  max-width: none !important;
  background: #F7F8FA;
`;

const Sidebar = styled.aside`
  width: 240px;
  background: #FFFFFF;
  border-right: 1px solid #EAEDF3;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: fixed;
  top: 0; left: 0; bottom: 0;
  z-index: 10;
`;

const LogoArea = styled.button`
  display: flex; align-items: center; gap: 10px;
  padding: 18px 20px;
  border-bottom: 1px solid #EAEDF3;
  text-align: left;
`;

const LogoIcon = styled.div`
  width: 34px; height: 34px; border-radius: 9px;
  background: #4A7CFE; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800;
`;

const LogoText = styled.div` font-size: 15px; font-weight: 800; color: #1A1A2E; line-height: 1.1; `;
const LogoSub = styled.div` font-size: 11px; font-weight: 600; color: #9CA3AF; margin-top: 2px; `;

const AcademyPicker = styled.div` position: relative; padding: 12px; `;

const PickerBtn = styled.button`
  width: 100%; display: flex; align-items: center; gap: 10px;
  padding: 10px; border-radius: 10px; border: 1px solid #EAEDF3;
  background: #FCFCFD; cursor: ${({ $multi }) => ($multi ? 'pointer' : 'default')};
  &:hover { background: ${({ $multi }) => ($multi ? '#F4F6FB' : '#FCFCFD')}; }
`;

const PickerThumb = styled.div`
  width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
  background: ${({ $url }) => ($url ? `#eee url(${$url}) center/cover` : '#E0E7FF')};
  color: #4A7CFE; font-weight: 800; font-size: 13px;
  display: flex; align-items: center; justify-content: center;
`;

const PickerInfo = styled.div` flex: 1; min-width: 0; text-align: left; `;
const PickerName = styled.div` font-size: 13px; font-weight: 700; color: #1F2937; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; `;
const PickerRole = styled.div` font-size: 11px; color: #9CA3AF; margin-top: 1px; `;

const PickerMenu = styled.div`
  position: absolute; left: 12px; right: 12px; top: 100%;
  background: #fff; border: 1px solid #EAEDF3; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.1); padding: 6px; z-index: 20;
`;

const PickerItem = styled.button`
  width: 100%; display: flex; align-items: center; justify-content: space-between;
  padding: 9px 10px; border-radius: 8px; font-size: 13px; font-weight: ${({ $on }) => ($on ? 700 : 500)};
  color: #374151;
  &:hover { background: #F4F6FB; }
`;

const Nav = styled.nav` flex: 1; padding: 8px; `;

const NavItem = styled.button`
  width: 100%; display: flex; align-items: center; gap: 11px;
  padding: 11px 14px; border-radius: 9px;
  color: ${({ $active }) => ($active ? '#1D4ED8' : '#374151')};
  background: ${({ $active }) => ($active ? '#EEF2FF' : 'transparent')};
  font-size: 14px; font-weight: ${({ $active }) => ($active ? 700 : 500)};
  margin-bottom: 2px; transition: background 0.15s;
  &:hover { background: ${({ $active }) => ($active ? '#EEF2FF' : '#F9FAFB')}; }
`;

const SidebarFooter = styled.div` padding: 12px; border-top: 1px solid #EAEDF3; `;

const FooterBtn = styled.button`
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 7px;
  padding: 10px; border-radius: 8px; color: #6B7280; font-size: 13px; font-weight: 600;
  &:hover { background: #F9FAFB; color: #374151; }
`;

const MainArea = styled.div` flex: 1; margin-left: 240px; display: flex; flex-direction: column; min-width: 0; `;

const TopBar = styled.header`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 28px; background: #fff; border-bottom: 1px solid #EAEDF3;
  position: sticky; top: 0; z-index: 5;
`;

const TopTitle = styled.h1` font-size: 18px; font-weight: 800; color: #1A1A2E; display: flex; align-items: center; gap: 10px; `;

const PendingTag = styled.span`
  font-size: 11px; font-weight: 700; color: #B45309;
  background: #FEF3C7; padding: 3px 9px; border-radius: 11px;
`;

const ProfileChip = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 4px 14px 4px 4px; border: 1px solid #EAEDF3; border-radius: 22px;
  span { font-size: 13px; font-weight: 600; color: #374151; }
`;

const ProfileAvatar = styled.div`
  width: 30px; height: 30px; border-radius: 50%;
  background: #4A7CFE; color: #fff; font-size: 13px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
`;

const Content = styled.main` flex: 1; padding: 28px; max-width: 1280px; width: 100%; `;
